import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { CURRENT_SITE } from '../../site-config';
import { AtelierAnimation } from './AtelierAnimation';
import { LanguageSwitcher } from './LanguageSwitcher';

const VERIFICATION_CODE = import.meta.env.VITE_LOGIN_VERIFICATION_CODE || '';

// =============================================================================
// Visual system is Home5's (dark slate + violet/emerald accents, DM Sans, the
// same card, button and glow treatments) so sign-in reads as the same product
// as the marketing home it is reached from. Copy, fields, validation and the
// returnUrl behaviour are unchanged — this is presentation only.
// =============================================================================

const Logo: React.FC<{ size?: number }> = ({ size = 1 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 * size }}>
    <svg width={30 * size} height={30 * size} viewBox="0 0 32 32" fill="none">
      <path
        d="M16 2C8.268 2 2 8.268 2 16s6.268 14 14 14 14-6.268 14-14S23.732 2 16 2z"
        fill="#a78bfa"
      />
      <path
        d="M10 18c0-3.314 2.686-6 6-6s6 2.686 6 6"
        stroke="#0f172a"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path
        d="M13 15c0-1.657 1.343-3 3-3s3 1.343 3 3"
        stroke="#0f172a"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="16" cy="22" r="1.5" fill="#0f172a" />
    </svg>
    <span
      style={{
        fontFamily: "'DM Sans', sans-serif",
        fontWeight: 700,
        fontSize: 18 * size,
        color: '#e2e8f0',
        letterSpacing: '-0.02em',
      }}
    >
      {CURRENT_SITE.displayName}
    </span>
  </div>
);

export const LoginForm: React.FC = () => {
  const { t } = useTranslation('navigation');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const { login, isLoading, error, clearError } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { locale } = useParams<{ locale: string }>();

  const requiresVerification = VERIFICATION_CODE.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setVerificationError('');

    // Validate verification code only when configured
    if (requiresVerification && verificationCode.toLowerCase() !== VERIFICATION_CODE.toLowerCase()) {
      setVerificationError(t('loginForm.verificationError'));
      return;
    }

    try {
      await login({ email, password });
      // After login, redirect to returnUrl if present
      const returnUrl = searchParams.get('returnUrl');
      if (returnUrl) {
        navigate(returnUrl, { replace: true });
      }
    } catch (error) {
      // Error is handled by the auth context
      console.error('Login failed:', error);
    }
  };

  return (
    <div className="h5auth-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&display=swap');
        .h5auth-root *{box-sizing:border-box;margin:0;padding:0}
        .h5auth-root{position:relative;overflow:hidden;font-family:'DM Sans',sans-serif;background:linear-gradient(135deg,#0f172a 0%,#1e1b4b 52%,#0f172a 100%);color:#e2e8f0;min-height:100vh;display:flex;align-items:center;justify-content:center;padding:56px 24px}

        /* Ambient field — same glow + mesh pair the Home5 hero uses. */
        .h5auth-glow{position:absolute;border-radius:50%;filter:blur(96px);pointer-events:none}
        .h5auth-g1{width:520px;height:520px;background:radial-gradient(circle,rgba(167,139,250,.45),transparent 68%);top:-180px;left:-140px;animation:h5authDrift 20s ease-in-out infinite}
        .h5auth-g2{width:480px;height:480px;background:radial-gradient(circle,rgba(52,211,153,.28),transparent 70%);bottom:-200px;right:-160px;animation:h5authDrift 25s ease-in-out 2s infinite reverse}
        @keyframes h5authDrift{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(46px,-34px) scale(1.09)}}
        .h5auth-mesh{position:absolute;inset:0;background-image:linear-gradient(rgba(255,255,255,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.04) 1px,transparent 1px);background-size:56px 56px;mask-image:radial-gradient(ellipse 78% 62% at 50% 40%,#000 26%,transparent 76%);-webkit-mask-image:radial-gradient(ellipse 78% 62% at 50% 40%,#000 26%,transparent 76%);pointer-events:none}

        .h5auth-shell{position:relative;z-index:2;width:100%;max-width:460px}
        .h5auth-lang{display:flex;justify-content:flex-end;margin-bottom:18px}
        .h5auth-head{text-align:center;margin-bottom:26px}
        .h5auth-logo{display:flex;justify-content:center;margin-bottom:18px}
        .h5auth-title{font-size:clamp(26px,4vw,34px);font-weight:800;letter-spacing:-.03em;line-height:1.12;color:#f8fafc}
        .h5auth-sub{font-size:14.5px;line-height:1.7;color:#94a3b8;margin-top:10px}

        .h5auth-anim{margin-bottom:22px}

        /* Card, field and button tokens lifted from Home5 (.h5-card / .h5-btn). */
        .h5auth-card{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:20px;padding:30px 28px}
        .h5auth-form{display:flex;flex-direction:column;gap:18px}
        .h5auth-field{display:flex;flex-direction:column;gap:7px}
        .h5auth-label{font-size:13px;font-weight:600;letter-spacing:-.01em;color:#cbd5e1}
        .h5auth-input{width:100%;font-family:inherit;font-size:15px;color:#e2e8f0;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.12);border-radius:12px;padding:13px 15px;transition:border-color .25s,background .25s,box-shadow .25s}
        .h5auth-input::placeholder{color:#64748b}
        .h5auth-input:hover{border-color:rgba(255,255,255,.2)}
        .h5auth-input:focus{outline:none;background:rgba(255,255,255,.06);border-color:rgba(167,139,250,.6);box-shadow:0 0 0 3px rgba(167,139,250,.16)}

        .h5auth-error{background:rgba(248,113,113,.09);border:1px solid rgba(248,113,113,.3);color:#fca5a5;border-radius:12px;padding:12px 15px;font-size:13.5px;line-height:1.6}

        .h5auth-btn{display:inline-flex;align-items:center;justify-content:center;gap:10px;width:100%;font-family:inherit;font-weight:700;font-size:15px;padding:14px 30px;border-radius:12px;border:none;cursor:pointer;background:linear-gradient(135deg,#a78bfa,#8b5cf6);color:#fff;box-shadow:0 8px 28px rgba(139,92,246,.32);transition:transform .25s,box-shadow .25s,background .25s}
        .h5auth-btn:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 14px 38px rgba(139,92,246,.46)}
        .h5auth-btn:disabled{opacity:.55;cursor:not-allowed;box-shadow:none;transform:none}
        .h5auth-spinner{width:16px;height:16px;border-radius:50%;border:2px solid rgba(255,255,255,.35);border-top-color:#fff;animation:h5authSpin .7s linear infinite}
        @keyframes h5authSpin{to{transform:rotate(360deg)}}

        .h5auth-foot{margin-top:20px;text-align:center;font-size:13.5px;color:#94a3b8}
        .h5auth-link{color:#a78bfa;font-weight:600;text-decoration:underline;text-underline-offset:3px}
        .h5auth-link:hover{color:#c4b5fd}

        @media(max-width:520px){.h5auth-card{padding:26px 22px}}
        @media(prefers-reduced-motion:reduce){
          .h5auth-root *,.h5auth-root *::before,.h5auth-root *::after{animation-duration:.001ms!important;animation-iteration-count:1!important;transition-duration:.001ms!important}
        }
      `}</style>

      <div className="h5auth-glow h5auth-g1" />
      <div className="h5auth-glow h5auth-g2" />
      <div className="h5auth-mesh" />

      <div className="h5auth-shell">
        {/* Sign-in is reached before the dashboard shell exists, so the locale
            toggle has to live on the page itself. */}
        <div className="h5auth-lang">
          <LanguageSwitcher variant="dark" />
        </div>

        <div className="h5auth-head">
          <div className="h5auth-logo">
            <Logo />
          </div>
          <h2 className="h5auth-title">{CURRENT_SITE.displayName}</h2>
          <p className="h5auth-sub">{t('loginForm.subtitle')}</p>
        </div>

        {CURRENT_SITE.siteId === 'sewing' && (
          <div className="h5auth-anim">
            <AtelierAnimation />
          </div>
        )}

        <div className="h5auth-card">
          <form className="h5auth-form" onSubmit={handleSubmit}>
            <div className="h5auth-field">
              <label className="h5auth-label" htmlFor="login-email">
                {t('loginForm.emailLabel')}
              </label>
              <input
                id="login-email"
                className="h5auth-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder={t('loginForm.emailPlaceholder')}
              />
            </div>

            <div className="h5auth-field">
              <label className="h5auth-label" htmlFor="login-password">
                {t('loginForm.passwordLabel')}
              </label>
              <input
                id="login-password"
                className="h5auth-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder={t('loginForm.passwordPlaceholder')}
              />
            </div>

            {requiresVerification && (
              <div className="h5auth-field">
                <label className="h5auth-label" htmlFor="login-verification">
                  {t('loginForm.verificationCodeLabel')}
                </label>
                <input
                  id="login-verification"
                  className="h5auth-input"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  required
                  placeholder={t('loginForm.verificationCodePlaceholder')}
                  autoComplete="off"
                />
              </div>
            )}

            {verificationError && (
              <div className="h5auth-error">{verificationError}</div>
            )}

            {error && <div className="h5auth-error">{error}</div>}

            <button
              type="submit"
              className="h5auth-btn"
              aria-busy={isLoading}
              disabled={isLoading || !email || !password || (requiresVerification && !verificationCode)}
            >
              {isLoading && <span className="h5auth-spinner" aria-hidden="true" />}
              {isLoading ? t('loginForm.signingIn') : t('loginForm.signInButton')}
            </button>
          </form>

          <div className="h5auth-foot">
            <Link to={`/${locale || 'en'}/forgot-password`} className="h5auth-link">
              {t('loginForm.forgotPassword', 'Forgot your password?')}
            </Link>
          </div>

          <div className="h5auth-foot">
            {t('loginForm.noAccount', "Don't have an account?")}{' '}
            <Link
              to={`/${locale || 'en'}/register${searchParams.get('returnUrl') ? `?returnUrl=${searchParams.get('returnUrl')}` : ''}`}
              className="h5auth-link"
            >
              {t('loginForm.signUp', 'Sign up')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
